import { useMemo, useReducer } from 'react';
import type { HedgeBookEntry } from '../api/hedge-book';

// Balance tolerance per scope §4 — 0.01 MT for metals, 1 unit for FX.
export const METAL_BALANCE_TOLERANCE = 0.01;
export const FX_BALANCE_TOLERANCE = 1;

export interface AllocateFillFormState {
  metal: string;
  currencyPair: string;
  direction: string;
  totalQuantity: string;
  executedPrice: string;
  executedPriceCurrency: string;
  executionDate: string;
  promptDate: string;
  exchange: string;
  brokerName: string;
  contractReference: string;
  notes: string;
  // hedgeRequestId -> per-row allocation input
  perRow: Record<string, string>;
}

export const ALLOCATE_INITIAL_STATE: AllocateFillFormState = {
  metal: '',
  currencyPair: '',
  direction: '',
  totalQuantity: '',
  executedPrice: '',
  executedPriceCurrency: 'USD',
  executionDate: new Date().toISOString().slice(0, 10),
  promptDate: '',
  exchange: 'LME',
  brokerName: 'StoneX',
  contractReference: '',
  notes: '',
  perRow: {},
};

type Action =
  | { type: 'set'; key: keyof AllocateFillFormState; value: string }
  | { type: 'setAllocation'; hedgeRequestId: string; value: string }
  | { type: 'reset' }
  | { type: 'clearAllocations' };

function dropKey(perRow: Record<string, string>, key: string): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [k, v] of Object.entries(perRow)) {
    if (k !== key) result[k] = v;
  }
  return result;
}

function reducer(state: AllocateFillFormState, action: Action): AllocateFillFormState {
  switch (action.type) {
    case 'set':
      // Setting metal, currency pair, or direction implicitly clears stale
      // per-row inputs from a previous filter so the residual reads correctly.
      if (action.key === 'metal' || action.key === 'currencyPair' || action.key === 'direction') {
        return { ...state, [action.key]: action.value, perRow: {} };
      }
      return { ...state, [action.key]: action.value };
    case 'setAllocation': {
      if (action.value.trim().length === 0) {
        return { ...state, perRow: dropKey(state.perRow, action.hedgeRequestId) };
      }
      return {
        ...state,
        perRow: { ...state.perRow, [action.hedgeRequestId]: action.value },
      };
    }
    case 'clearAllocations':
      return { ...state, perRow: {} };
    case 'reset':
      return ALLOCATE_INITIAL_STATE;
  }
}

export function useAllocateFillState() {
  const [state, dispatch] = useReducer(reducer, ALLOCATE_INITIAL_STATE);
  return { state, dispatch };
}

export interface ResidualSummary {
  total: number;
  allocated: number;
  residual: number;
  withinTolerance: boolean;
  positiveAllocations: { hedgeRequestId: string; quantity: number }[];
}

export function summarizeResidual(
  state: AllocateFillFormState,
  tolerance: number,
): ResidualSummary {
  const total = parsePositive(state.totalQuantity) ?? 0;
  const positiveAllocations = Object.entries(state.perRow)
    .map(([hedgeRequestId, raw]) => ({
      hedgeRequestId,
      quantity: parsePositive(raw) ?? 0,
    }))
    .filter((row) => row.quantity > 0);
  const allocated = positiveAllocations.reduce((acc, row) => acc + row.quantity, 0);
  const residual = total - allocated;
  return {
    total,
    allocated,
    residual,
    withinTolerance: Math.abs(residual) <= tolerance && total > 0 && positiveAllocations.length > 0,
    positiveAllocations,
  };
}

function parsePositive(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export interface CandidateFilter {
  metal: string;
  direction: string;
}

export function filterCandidates(
  entries: HedgeBookEntry[],
  filter: CandidateFilter,
): HedgeBookEntry[] {
  return entries.filter((entry) => {
    if (entry.status !== 'Approved') return false;
    if (filter.metal && entry.hedgeMetal !== filter.metal) return false;
    if (filter.direction && entry.direction !== filter.direction) return false;
    return true;
  });
}

export function useCandidateRows(
  entries: HedgeBookEntry[] | undefined,
  filter: CandidateFilter,
): HedgeBookEntry[] {
  return useMemo(() => filterCandidates(entries ?? [], filter), [entries, filter]);
}
